import React, { useState, useCallback, useEffect } from "react";
import {
  AppCSS,
  Lbl,
  Spacer,
} from "../../../../components";
import { RowDiv, ColDiv } from "../../../../components/misc/misc.styled";
import { FC } from "react";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { CalendarToday, CheckCircle, RadioButtonUnchecked, ArrowBack, ArrowForward } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import { TextField } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { SELECT_INSTALLATION_DATE } from "../../../../apolloClient/operation/mutations/mutation";
import { DayPicker } from "react-day-picker";
import type { DateRange } from "react-day-picker";
import { format } from "date-fns";
import "react-day-picker/dist/style.css";

// Extend dayjs with the utc plugin
dayjs.extend(utc);

// Responsive calendar styles
const calendarStyles = `
  .calendar-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .responsive-calendar {
    --rdp-cell-size: 40px;
  }
  .responsive-calendar .rdp-month {
    margin: 0;
  }
  .responsive-calendar .rdp-table {
    border-collapse: collapse;
  }
  @media (max-width: 768px) {
    .responsive-calendar {
      --rdp-cell-size: 40px;
      min-width: 320px;
    }
    .responsive-calendar .rdp-caption_label {
      font-size: 15px;
    }
    .responsive-calendar .rdp-dropdown {
      font-size: 14px;
    }
    .responsive-calendar .rdp-head_cell {
      font-size: 12px;
      padding: 4px;
    }
    .responsive-calendar .rdp-cell {
      padding: 2px;
    }
    .responsive-calendar .rdp-day {
      font-size: 13px;
    }
    .responsive-calendar .rdp-nav_button {
      width: 28px;
      height: 28px;
    }
  }
`;

interface ScheduleInstallationProps {
  handleRefetchProjectDetails: Function;
  projectData?: CustomerProjectDetails;
}

export interface InstallationInput {
  projectId: number;
  date?: string;
  fromDate?: string;
  toDate?: string;
  remarks?: string;
  message?: string;
  email?: string;
}


const ScheduleInstallation: FC<ScheduleInstallationProps> = ({
  handleRefetchProjectDetails,
  projectData,
}) => {
  const { id: projectId } = useParams<{ id: string }>();
  const [selected, setSelected] = useState<Date>();
  const [range, setRange] = useState<DateRange | undefined>();
  const [remarks, setRemarks] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [changeDate, setChangeDate] = useState<boolean>(true);
  const [isScheduling, setIsScheduling] = useState<boolean>(false);

  // Check if installation date already exists
  const existingInstallationDate = projectData?.installationDetails?.installationDate;
  const existingFromDate = projectData?.installationDetails?.fromDate;
  const existingToDate = projectData?.installationDetails?.toDate;
  const installationId = projectData?.installationDetails?.id;

  // Apollo mutation
  const [selectInstallationDate] = useMutation<
    {
      selectInstallation: boolean;
    },
    { input: InstallationInput }
  >(SELECT_INSTALLATION_DATE);

  useEffect(() => {
    if (existingInstallationDate || (existingFromDate && existingToDate)) {
      setChangeDate(false);
    }
  }, [existingInstallationDate, existingFromDate, existingToDate]);

  // Get date 7 days from today (minimum allowed)
  const getDate7DaysFromToday = () => {
    const today = new Date();
    const sevenDaysFromToday = new Date(today);
    sevenDaysFromToday.setDate(today.getDate() + 7);
    return sevenDaysFromToday;
  };

  // Get date 2 months from today (maximum allowed)
  const getDate2MonthsFromToday = () => {
    const today = new Date();
    const twoMonthsFromToday = new Date(today);
    twoMonthsFromToday.setMonth(today.getMonth() + 2);
    return twoMonthsFromToday;
  };

  // Format existing installation date for display
  const makeDate = () => {
    if (existingFromDate && existingToDate) {
      return (
        dayjs.utc(existingFromDate).format("ddd MMM DD YYYY") +
        " - " +
        dayjs.utc(existingToDate).format("ddd MMM DD YYYY")
      );
    } else if (existingInstallationDate) {
      return dayjs.utc(existingInstallationDate).format("ddd MMM DD YYYY");
    } else {
      return "No date selected";
    }
  };

  const scheduleTheInstall = useCallback(async (installation: InstallationInput) => {
    if (!projectId) {
      toast("Project ID not found", {
        type: "error",
        position: "top-right",
      });
      return;
    }

    setIsScheduling(true);

    try {
      await selectInstallationDate({ variables: { input: installation } });
      
      toast("Installation date selected successfully!", {
        type: "success",
        position: "top-right",
      });

      // Refetch project details to get updated data
      await handleRefetchProjectDetails();
      
      setChangeDate(false);
    } catch (error) {
      console.error("Error scheduling installation:", error);
      toast("Error selecting or updating date. Please try again.", {
        type: "error",
        position: "top-right",
      });
    } finally {
      setIsScheduling(false);
    }
  }, [projectId, selectInstallationDate, handleRefetchProjectDetails]);

  const handleConfirmDate = useCallback(() => {
    if (range?.from && range?.to && projectId) {
      scheduleTheInstall({
        projectId: parseInt(projectId, 10),
        fromDate: range.from.toISOString(),
        toDate: range.to.toISOString(),
        remarks: remarks.trim() || undefined,
        message: message.trim() || undefined,
        email: email.trim() || undefined,
      });
    }
  }, [range, remarks, message, email, projectId, scheduleTheInstall]);

  const ScheduleInfo = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        marginTop: "16px",
        padding: "0 8px",
      }}
    >
      <p
        style={{
          fontSize: "14px",
          color: "#6b7280",
          textAlign: "center",
          lineHeight: "1.6",
          maxWidth: "600px",
          margin: 0,
        }}
      >
        Installs can be scheduled as soon as 7 days into the future. Your
        project manager will reach out to confirm the date and notify you of
        the expected time of arrival for your crew. 50% of installs are
        completed within 1 day but we ask that you plan on 3 days for
        installation completion. If you have gutters on your project, your
        project manager will coordinate their install the day after your roof
        installation has been completed.
      </p>
    </div>
  );

  return (
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f8fafc",
        minHeight: "100%",
        width: "100%",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          <CalendarToday style={{ fontSize: "28px", color: "#1976d2" }} />
          <h1
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#1f2937",
              margin: 0,
            }}
          >
            Schedule Installation
          </h1>
        </div>
        <p
          style={{
            fontSize: "14px",
            color: "#6b7280",
            margin: 0,
            lineHeight: "1.5",
          }}
        >
          Select your preferred installation date range. You can schedule installations
          between 7 days and 2 months from today.
        </p>
      </div>

      {/* Main Content */}
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        {/* Installation Date Card */}
        <ColDiv
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            maxWidth: "1200px",
            width: "100%",
            height: "fit-content",
          }}
        >
          <RowDiv style={{ justifyContent: "space-between", alignItems: window.innerWidth < 768 ? "flex-start" : "center", marginBottom: "16px", flexDirection: window.innerWidth < 768 ? "column" : "row", gap: window.innerWidth < 768 ? "12px" : "0" }}>
            <ColDiv style={{ gap: "4px" }}>
              <Lbl
                title="Installation Date"
                size={16}
                bold={500}
                color="#1f2937"
              />
              <Lbl
                title={(existingInstallationDate || (existingFromDate && existingToDate)) ? `Current: ${makeDate()}` : "No date selected"}
                size={14}
                color="#6b7280"
              />
            </ColDiv>
            
            {/* Status Badge */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                backgroundColor: (existingInstallationDate || (existingFromDate && existingToDate))
                  ? "rgba(16, 185, 129, 0.1)" 
                  : "rgba(245, 158, 11, 0.1)",
                color: (existingInstallationDate || (existingFromDate && existingToDate)) ? "#059669" : "#f59e0b",
              }}
            >
              {(existingInstallationDate || (existingFromDate && existingToDate)) ? (
                <>
                  <CheckCircle style={{ width: 16, height: 16 }} />
                  <span>Scheduled</span>
                </>
              ) : (
                <>
                  <RadioButtonUnchecked style={{ width: 16, height: 16 }} />
                  <span>Pending</span>
                </>
              )}
            </div>
          </RowDiv>

          {changeDate ? (
            // Date Selection View
            <ColDiv style={{ gap: "24px" }}>
              <div style={{ borderTop: "1px solid #e5e7eb", width: "100%" }} />
              
              <style>{calendarStyles}</style>
              <div style={{ display: "flex", flexDirection: window.innerWidth < 768 ? "column" : "row", gap: window.innerWidth < 768 ? "20px" : "24px", alignItems: "flex-start" }}>
                {/* Left side - Calendar */}
                <div style={{ 
                  flex: window.innerWidth < 768 ? "1" : "0 0 auto", 
                  width: "100%",
                  maxWidth: window.innerWidth < 768 ? "100%" : "340px",
                }}>
                  <div style={{ fontSize: window.innerWidth < 768 ? "16px" : "14px", fontWeight: 500, color: "#1f2937", marginBottom: "8px" }}>
                    Select Installation Date Range
                  </div>
                  <p style={{ margin: "0 0 12px 0", fontSize: window.innerWidth < 768 ? "13px" : "12px", color: "#6b7280", lineHeight: "1.4" }}>
                    Available dates: {getDate7DaysFromToday().toLocaleDateString()} - {getDate2MonthsFromToday().toLocaleDateString()}
                  </p>
                  <div style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: window.innerWidth < 400 ? "8px 4px" : "10px",
                  }}>
                    <div className="calendar-wrapper">
                      <DayPicker
                        className="responsive-calendar"
                        fromDate={getDate7DaysFromToday()}
                        toDate={getDate2MonthsFromToday()}
                        captionLayout="dropdown"
                        mode="range"
                        selected={range}
                        onSelect={setRange}
                        disabled={[
                          { before: getDate7DaysFromToday() },
                          (date: Date) => date.getDay() === 0
                        ]}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: "10px", textAlign: "center", fontSize: window.innerWidth < 768 ? "14px" : "13px", color: "#6b7280", padding: "0 8px" }}>
                    {range?.from && range?.to
                      ? `Selected: ${format(range.from, "PP")} – ${format(range.to, "PP")}`
                      : "Please select a start and end date."}
                  </div>
                </div>

                {/* Right side - Form inputs */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px", width: window.innerWidth < 768 ? "100%" : "auto" }}>
                  <div>
                    <div style={{ fontSize: window.innerWidth < 768 ? "15px" : "14px", fontWeight: 500, color: "#1f2937", marginBottom: "8px" }}>
                      Remarks (optional)
                    </div>
                    <TextField
                      fullWidth
                      multiline
                      rows={window.innerWidth < 768 ? 4 : 3}
                      placeholder="Let us know if there are any dates you are unavailable or any special instructions."
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: window.innerWidth < 768 ? '15px' : '14px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: window.innerWidth < 768 ? '12px' : '14px',
                        },
                        '& textarea::placeholder': {
                          opacity: 1,
                          fontSize: window.innerWidth < 768 ? '14px' : 'inherit',
                        },
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: window.innerWidth < 768 ? "15px" : "14px", fontWeight: 500, color: "#1f2937", marginBottom: "8px" }}>
                      Email (optional)
                    </div>
                    <TextField
                      fullWidth
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: window.innerWidth < 768 ? '15px' : '14px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: window.innerWidth < 768 ? '12px 14px' : '14px',
                        },
                        '& input::placeholder': {
                          opacity: 1,
                          fontSize: window.innerWidth < 768 ? '14px' : 'inherit',
                        },
                      }}
                    />
                  </div>

                  <div>
                    <div style={{ fontSize: window.innerWidth < 768 ? "15px" : "14px", fontWeight: 500, color: "#1f2937", marginBottom: "8px" }}>
                      Custom Message (optional)
                    </div>
                    <TextField
                      fullWidth
                      multiline
                      rows={window.innerWidth < 768 ? 4 : 3}
                      placeholder="Enter any custom message for the customer."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: window.innerWidth < 768 ? '15px' : '14px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: window.innerWidth < 768 ? '12px' : '14px',
                        },
                        '& textarea::placeholder': {
                          opacity: 1,
                          fontSize: window.innerWidth < 768 ? '14px' : 'inherit',
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              <ColDiv style={{ gap: "16px" }}>

                <RowDiv style={{ gap: "12px", justifyContent: (range?.from && range?.to) ? "space-between" : "flex-end" }}>
                  {(range?.from && range?.to) && (
                    <button
                      onClick={() => {
                        setRange(undefined);
                        setRemarks("");
                        setMessage("");
                        setEmail("");
                        setChangeDate(false);
                      }}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        border: "1px solid #d1d5db",
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        color: "#1f2937",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Cancel
                    </button>
                  )}

                  {(range?.from && range?.to) && (
                    <button
                      onClick={handleConfirmDate}
                      disabled={isScheduling}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        border: "none",
                        cursor: isScheduling ? "not-allowed" : "pointer",
                        backgroundColor: isScheduling ? "#9ca3af" : "#3b82f6",
                        color: "#fff",
                        transition: "all 0.2s ease",
                        opacity: isScheduling ? 0.7 : 1,
                        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      {isScheduling ? "Scheduling..." : "Confirm Date Range"}
                    </button>
                  )}

                  {!(range?.from && range?.to) && (
                    <button
                      onClick={() => setChangeDate(false)}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        border: "1px solid #d1d5db",
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        color: "#1f2937",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Close
                    </button>
                  )}
                </RowDiv>
              </ColDiv>
            </ColDiv>
          ) : (
            // Date Display View
            <ColDiv style={{ gap: "16px" }}>
              <div style={{ borderTop: "1px solid #e5e7eb", width: "100%" }} />
              
              <div style={{ display: "flex", flexDirection: window.innerWidth < 768 ? "column" : "row", justifyContent: "space-between", alignItems: window.innerWidth < 768 ? "stretch" : "center", gap: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 16px",
                    backgroundColor: (existingInstallationDate || (existingFromDate && existingToDate)) ? "rgba(16, 185, 129, 0.1)" : "#f3f4f6",
                    borderRadius: "8px",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <CalendarToday
                    style={{
                      width: 20,
                      height: 20,
                      color: (existingInstallationDate || (existingFromDate && existingToDate)) ? "#059669" : "#6b7280",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ 
                    fontSize: "14px",
                    fontWeight: 500,
                    color: (existingInstallationDate || (existingFromDate && existingToDate)) ? "#059669" : "#6b7280",
                    wordBreak: "break-word",
                    flex: 1,
                  }}>
                    {(existingInstallationDate || (existingFromDate && existingToDate)) ? makeDate() : "No date selected"}
                  </div>
                </div>

                <button
                  onClick={() => setChangeDate(true)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    border: "1px solid #3b82f6",
                    cursor: "pointer",
                    backgroundColor: "#fff",
                    color: "#3b82f6",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(existingInstallationDate || (existingFromDate && existingToDate)) ? "Change Date" : "Select Date"}
                  <ArrowForward style={{ width: 16, height: 16 }} />
                </button>
              </div>

              {/* Display Remarks if exists */}
              {projectData?.installationDetails?.remarks && (
                <div
                  style={{
                    padding: "12px 16px",
                    // backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Lbl
                    title="Remarks"
                    size={12}
                    bold={600}
                    color="#6b7280"
                  />
                  <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#1f2937", lineHeight: "1.5" }}>
                    {projectData.installationDetails.remarks}
                  </p>
                </div>
              )}
            </ColDiv>
          )}
        </ColDiv>
      </div>

      <Spacer size={3} direction="col" />
    </div>
  );
};

export { ScheduleInstallation };
